import { ListingCardListing } from '../../../ListingCard/types';
import { CouldBuyFormState, CouldBuySeed, createInitialFormState } from './types';
import { normalizeCriteriaValue } from './steps/criteriaOptions';

// Result of mapping a stored buy listing onto the could-buy wizard: the form
// pre-fill plus the seed the flow needs to reopen on the listing's building.
export interface CouldBuyEditPrefill {
  form: CouldBuyFormState;
  seed: CouldBuySeed;
}

// Map an existing buy listing (an off-market willingness row, ListingCardListing
// shape — used by both MyListings and the building manage menu) onto the
// CouldBuyFlow's edit pre-fill (JAA-304). A buy listing is always a single
// building interest, never an area interest, so only description/size/rooms
// carry over from the listing's own property_* fields; everything else stays
// the flow's default (no areas/circle, anchor building selected). Same role as
// useListingManagement.tsx's listingToDetails: a listing -> flow-prefill mapper
// feeding that flow's edit entry point, here for the buy side of JAA-304.
export function listingToCouldBuyForm(listing: ListingCardListing): CouldBuyEditPrefill {
  // The backend serves `criteria` for every buy listing, deriving it from the
  // flat property_* fields when the document predates it — so this reads one
  // shape and needs no fallback of its own. `floor` is the reason that matters:
  // it only ever exists in criteria, since property_floor means a storey number.
  const criteria = listing.criteria ?? {};
  const num = (v: unknown) => (v != null ? String(v) : '');
  const form: CouldBuyFormState = {
    ...createInitialFormState(),
    description: criteria.property_description ?? '',
    sizeMin: num(criteria.size_min),
    sizeMax: num(criteria.size_max),
    roomsMin: criteria.rooms_min ?? '',
    roomsFlexible: !!criteria.rooms_flexible,
    priceMin: num(criteria.price_min),
    priceMax: num(criteria.price_max),
    floor: criteria.floor ?? '',
    renovation: normalizeCriteriaValue(criteria.renovation_status) ?? '',
    amenities: criteria.amenities ?? [],
    // Digest notify flags stored on the willingness doc; the flow's defaults
    // (email on, SMS off) stand in for rows serialized without them.
    notifyEmail: listing.notify_email ?? true,
    notifySms: listing.notify_sms ?? false,
  };
  const seed: CouldBuySeed = {
    buildingId: listing.building_id ?? '',
    coordinates: null,
    address: {
      street: listing.address.street,
      houseNumber: listing.address.houseNumber,
      city: listing.address.city,
      country: listing.address.country ?? '',
    },
  };
  return { form, seed };
}
